import React, { useEffect, useMemo, useState } from "react"
import { createPortal } from "react-dom"
import { useIsFetching, useIsMutating } from "@tanstack/react-query"
import IconFont from "../elements/IconFont"
import { shouldShowGlobalMutationLoader } from "../../utils/pageLoaderPolicy"

export interface PageLoaderProps {
  open?: boolean
}

const PageLoader: React.FC<PageLoaderProps> = ({ open = false }: PageLoaderProps): React.ReactElement | null => {

  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  const isFetching = useIsFetching({
    predicate: (query) => {
      const queryKey = query.options.queryKey || []
      const isExcludedQuery = [
        'prismancy_list_experiments',
        'prismancy_list_user_invitations',
        'prismancy_current_experiment',
        'prismancy_activity_log'
      ].some(keyName => {
        return queryKey.includes(keyName)
      })

      if (isExcludedQuery) {
        return false
      }

      // Show global loader only for first fetch (no previously loaded data).
      const hasLoadedData = query.state.data !== undefined
      return hasLoadedData === false
    }
  })

  const isMutating = useIsMutating({
    predicate: (mutation) => shouldShowGlobalMutationLoader(mutation.options)
  })

  const shouldShow = useMemo(() => {
    if (isMutating > 0) {
      return true
    }
    if (isFetching) {
      return true
    }
    if (open) {
      return true
    }
    return false
  }, [open, isFetching, isMutating])

  useEffect(() => {
    if (shouldShow) {
      setShouldRender(true);
      // Small delay to trigger CSS transition
      requestAnimationFrame(() => {
        setIsVisible(true);
      });
    } else {
      setIsVisible(false);
      // Wait for fade-out animation to complete before unmounting
      const timer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timer);
    }
  }, [shouldShow]);

  if (!shouldRender) {
    return null
  }

  return createPortal(
    <div
      className={`ui-page-loader ui-layer-page-loader pointer-events-auto fixed inset-0 flex h-full w-full items-center justify-center bg-background-surface-01 transition-opacity duration-300 ease-in-out ${isVisible ? 'opacity-70' : 'opacity-0'
        }`}
    >
      <IconFont name="loader" size={32} className="pi-page-loader" />
    </div>,
    document.body
  )
}

export default PageLoader
